const ON_CHANGE_DEBOUNCE_TIMER = 300;

const PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update',
  quantityUpdate: 'quantity-update',
  optionValueSelectionChange: 'option-value-selection-change',
  variantChange: 'variant-change',
  cartError: 'cart-error',
  sectionRefreshed: 'section-refreshed',
};

const SECTION_REFRESH_RESOURCE_TYPE = {
  product: 'product',
};

let subscribers = {};

function subscribe(eventName, callback) {
  if (subscribers[eventName] === undefined) {
    subscribers[eventName] = [];
  }

  subscribers[eventName] = [...subscribers[eventName], callback];

  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter((cb) => {
      return cb !== callback;
    });
  };
}

function publish(eventName, data) {
  if (subscribers[eventName]) {
    subscribers[eventName].forEach((callback) => {
      callback(data);
    });
  }
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  if (summary.closest('header-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE' || event.key === 'Esc') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

// find and add nofollow to all the external links
// document.addEventListener('DOMContentLoaded', function () {
//   var links = document.links;
//   for (let i = 0, linksLength = links.length; i < linksLength; i++) {
//     if (links[i].hostname !== window.location.hostname) {
//       links[i].rel = 'nofollow noreferrer noopener';
//     }
//   }
// });

// class ProductForm extends HTMLElement {
//   constructor() {
//     super();

//     this.form = this.querySelector('form');
//     this.form.querySelector('[name=id]').disabled = false;
//     this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
//     this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
//     this.submitButton = this.querySelector('[type="submit"]');
//     this.productWrapper = this.form.dataset.productRef ? document.getElementById(this.form.dataset.productRef) : null;

//     if (this.productWrapper) this.subscriptionPriceOutput();
//   }

//   onSubmitHandler(evt) {
//     evt.preventDefault();
//     if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

//     this.handleErrorMessage();

//     this.submitButton.setAttribute('aria-disabled', true);
//     this.submitButton.classList.add('loading');
//     this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

//     const config = fetchConfig('javascript');
//     config.headers['X-Requested-With'] = 'XMLHttpRequest';
//     delete config.headers['Content-Type'];

//     const formData = new FormData(this.form);
//     if (this.cart) {
//       formData.append(
//         'sections',
//         this.cart.getSectionsToRender().map((section) => section.id)
//       );
//       formData.append('sections_url', window.location.pathname);
//       this.cart.setActiveElement(document.activeElement);
//     }
//     config.body = formData;

//     fetch(`${routes.cart_add_url}`, config)
//       .then((response) => response.json())
//       .then((response) => {
//         if (response.status) {
//           this.handleErrorMessage(response.description);

//           const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
//           if (!soldOutMessage) return;
//           this.submitButton.setAttribute('aria-disabled', true);
//           this.submitButton.querySelector('span').classList.add('hidden');
//           soldOutMessage.classList.remove('hidden');
//           this.error = true;
//           return;
//         } else if (!this.cart) {
//           window.location = window.routes.cart_url;
//           return;
//         }

//         this.error = false;
//         const quickAddModal = this.closest('quick-add-modal');
//         if (quickAddModal) {
//           document.body.addEventListener(
//             'modalClosed',
//             () => {
//               setTimeout(() => {
//                 this.cart.renderContents(response);
//               });
//             },
//             { once: true }
//           );
//           quickAddModal.hide(true);
//         } else {
//           this.cart.renderContents(response);
//         }
//       })
//       .catch((e) => {
//         console.error(e);
//       })
//       .finally(() => {
//         this.submitButton.classList.remove('loading');
//         if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
//         if (!this.error) this.submitButton.removeAttribute('aria-disabled');
//         this.querySelector('.loading-overlay__spinner').classList.add('hidden');
//         this.renderFreeShippingCTA();
//       });
//   }

//   renderFreeShippingCTA() {
//     const freeShippingSection = document.getElementById('section--free-shipping-cta');
//     if (!freeShippingSection) return;

//     const freeShippingThreshold = parseInt(freeShippingSection.dataset.threshold);
//     const freeShippingWrap = freeShippingSection.querySelector('.free-shipping-cta');
//     const freeShippingRemainder = freeShippingSection.querySelector('#freeShippingRemainder');
//     const remainderUSD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

//     fetch('/cart.js')
//       .then((response) => response.json())
//       .then((data) => {
//         if (data.total_price >= freeShippingThreshold) {
//           freeShippingWrap.classList.add('qualified');
//         } else {
//           freeShippingRemainder.innerHTML = remainderUSD.format((freeShippingThreshold - data.total_price) / 100);
//           freeShippingWrap.classList.remove('qualified');
//         }
//       });
//   }

//   // modifies PDP UI for the Appstle subscription purchase option
//   subscriptionPriceOutput() {
//     const addPriceUI = () => {
//       const priceWrapper = this.productWrapper.querySelector('.price__regular');
//       const priceCompare = document.createElement('s');
//       priceCompare.classList.add('price-subscribe', 'font-serif', 'text-light', 'visually-hidden');
//       const subscribeLabel = document.createElement('span');
//       subscribeLabel.classList.add('price-subscribe-label', 'small', 'visually-hidden');
//       subscribeLabel.innerText = 'Subscription';

//       priceWrapper.prepend(priceCompare);
//       priceWrapper.append(subscribeLabel);
//     };

//     const setPriceOutput = () => {
//       let planOneTime = null;
//       const planOptions = this.form.querySelectorAll('input[name=selling_plan]');
//       planOptions.forEach((option) => {
//         if (option.value === '') planOneTime = option;
//       });

//       const planSelected = this.form.querySelector('input[name=selling_plan]:checked');
//       const planSelectedAmt = planSelected.parentNode.querySelector('.appstle_subscription_amount').innerText;
//       const priceWrapper = this.productWrapper.querySelector('.price__regular');

//       priceWrapper.querySelector('.price-item').innerText = planSelectedAmt;

//       if (planSelected === planOneTime) {
//         priceWrapper.querySelector('.price-subscribe').classList.add('visually-hidden');
//         priceWrapper.querySelector('.price-subscribe-label').classList.add('visually-hidden');
//       } else {
//         const planOneTimeAmt = planOneTime.parentNode.querySelector('.appstle_subscription_amount').innerText;
//         priceWrapper.querySelector('.price-subscribe').innerText = planOneTimeAmt;
//         priceWrapper.querySelector('.price-subscribe').classList.remove('visually-hidden');
//         priceWrapper.querySelector('.price-subscribe-label').classList.remove('visually-hidden');
//       }
//     };

//     const setCTAlabels = (selected = true) => {
//       const btnBIN = this.form.querySelector('button.shopify-payment-button__button');
//       btnBIN.innerText = selected ? 'Subscribe' : 'Buy it now';
//     };

//     addPriceUI();

//     document.addEventListener('AppstleSubscription:SubscriptionWidget:SellingPlanDeSelected', (e) => {
//       setPriceOutput();
//       //setCTAlabels(false);
//     });
//     document.addEventListener('AppstleSubscription:SubscriptionWidget:SellingPlanSelected', (e) => {
//       setPriceOutput();
//       //setCTAlabels();
//       const appstleSelect = document.querySelector('select.appstle_select');
//       appstleSelect?.addEventListener('change', (e) => {
//         setPriceOutput();
//       });
//     });
//   }

//   handleErrorMessage(errorMessage = false) {
//     this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
//     if (!this.errorMessageWrapper) return;
//     this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

//     this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

//     if (errorMessage) {
//       this.errorMessage.textContent = errorMessage;
//     }
//   }
// }

// customElements.define('product-form', ProductForm);

// class ProductForm extends HTMLElement {
//   constructor() {
//     super();

//     this.form = this.querySelector('form');
//     this.form.querySelector('[name=id]').disabled = false;
//     this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
//     if (window.innerWidth >= 1024) {

//       this.cart = document.querySelector('cart-drawer-test');
//     } else {

//       this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
//     }
//     this.submitButton = this.querySelector('[type="submit"]');
//     this.productWrapper = this.form.dataset.productRef ? document.getElementById(this.form.dataset.productRef) : null;

//     if (this.productWrapper) this.subscriptionPriceOutput();
//   }



//   // onSubmitHandler(evt) {
//   //   evt.preventDefault();
//   //   if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

//   //   this.handleErrorMessage();
//   //   this.submitButton.setAttribute('aria-disabled', true);
//   //   this.submitButton.classList.add('loading');
//   //   this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

//   //   const config = fetchConfig('javascript');
//   //   config.headers['X-Requested-With'] = 'XMLHttpRequest';
//   //   delete config.headers['Content-Type'];

//   //   const formData = new FormData(this.form);
//   //   // include your drawer section id
//   //   formData.append('sections', 'cart-drawer-test');
//   //   formData.append('sections_url', window.location.pathname);

//   //   config.body = formData;

//   //   fetch(`${routes.cart_add_url}`, config)
//   //     .then((response) => response.json())
//   //     .then((response) => {
//   //       if (response.status) {
//   //         // handle sold out
//   //         this.handleErrorMessage(response.description);
//   //         this.error = true;
//   //         return;
//   //       }

//   //       // ✅ update drawer with the section HTML
//   //       const drawerHTML = response.sections['cart-drawer-test'];
//   //       document.querySelector('cart-drawer-test').innerHTML = drawerHTML;

//   //       // ✅ open the drawer (you can toggle a class on body)
//   //       document.body.classList.add('cart-open');
//   //     })
//   //     .catch((e) => {
//   //       console.error(e);
//   //     })
//   //     .finally(() => {
//   //       this.submitButton.classList.remove('loading');
//   //       if (!this.error) this.submitButton.removeAttribute('aria-disabled');
//   //       this.querySelector('.loading-overlay__spinner').classList.add('hidden');
//   //     });
//   // }


//   onSubmitHandler(evt) {
//     evt.preventDefault();
//     if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

//     // ✅ common pre‑loader
//     this.handleErrorMessage();
//     this.submitButton.setAttribute('aria-disabled', true);
//     this.submitButton.classList.add('loading');
//     this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

//     // ✅ check viewport
//     if (window.innerWidth < 1024) {
//       // 📱 ----- MOBILE CODE -----
//       const config = fetchConfig('javascript');
//       config.headers['X-Requested-With'] = 'XMLHttpRequest';
//       delete config.headers['Content-Type'];

//       const formData = new FormData(this.form);
//       if (this.cart) {
//         formData.append(
//           'sections',
//           this.cart.getSectionsToRender().map((section) => section.id)
//         );
//         formData.append('sections_url', window.location.pathname);
//         this.cart.setActiveElement(document.activeElement);
//       }
//       config.body = formData;

//       fetch(`${routes.cart_add_url}`, config)
//         .then((response) => response.json())
//         .then((response) => {
//           if (response.status) {
//             this.handleErrorMessage(response.description);
//             const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
//             if (soldOutMessage) {
//               this.submitButton.setAttribute('aria-disabled', true);
//               this.submitButton.querySelector('span').classList.add('hidden');
//               soldOutMessage.classList.remove('hidden');
//             }
//             this.error = true;
//             return;
//           } else if (!this.cart) {
//             window.location = window.routes.cart_url;
//             return;
//           }

//           this.error = false;
//           const quickAddModal = this.closest('quick-add-modal');
//           if (quickAddModal) {
//             document.body.addEventListener(
//               'modalClosed',
//               () => {
//                 setTimeout(() => {
//                   this.cart.renderContents(response);
//                 });
//               }, {
//                 once: true
//               }
//             );
//             quickAddModal.hide(true);
//           } else {
//             this.cart.renderContents(response);
//           }
//         })
//         .catch((e) => console.error(e))
//         .finally(() => {
//           this.submitButton.classList.remove('loading');
//           if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
//           if (!this.error) this.submitButton.removeAttribute('aria-disabled');
//           this.querySelector('.loading-overlay__spinner').classList.add('hidden');
//           this.renderFreeShippingCTA();
//         });

//     } else {
//       // 💻 ----- DESKTOP CODE -----
//       const config = fetchConfig('javascript');
//       config.headers['X-Requested-With'] = 'XMLHttpRequest';
//       delete config.headers['Content-Type'];

//       const formData = new FormData(this.form);
//       // include your drawer section id for desktop
//       formData.append('sections', 'cart-drawer-test');
//       formData.append('sections_url', window.location.pathname);

//       config.body = formData;

//       fetch(`${routes.cart_add_url}`, config)
//         .then((response) => response.json())
//         .then((response) => {
//           console.log('response')
//           console.log(response)
//           if (response.status) {
//             this.handleErrorMessage(response.description);
//             this.error = true;
//             return;
//           }

//           const drawerHTML = response.sections['cart-drawer-test'];
//           document.querySelector('cart-drawer-test').innerHTML = drawerHTML;
//           document.body.classList.add('cart-open');
//         })
//         .catch((e) => console.error(e))
//         .finally(() => {
//           this.submitButton.classList.remove('loading');
//           if (!this.error) this.submitButton.removeAttribute('aria-disabled');
//           this.querySelector('.loading-overlay__spinner').classList.add('hidden');
//         });
//     }
//   }




//   renderFreeShippingCTA() {
//     const freeShippingSection = document.getElementById('section--free-shipping-cta');
//     if (!freeShippingSection) return;

//     const freeShippingThreshold = parseInt(freeShippingSection.dataset.threshold);
//     const freeShippingWrap = freeShippingSection.querySelector('.free-shipping-cta');
//     const freeShippingRemainder = freeShippingSection.querySelector('#freeShippingRemainder');
//     const remainderUSD = new Intl.NumberFormat('en-US', {
//       style: 'currency',
//       currency: 'USD'
//     });

//     fetch('/cart.js')
//       .then((response) => response.json())
//       .then((data) => {
//         if (data.total_price >= freeShippingThreshold) {
//           freeShippingWrap.classList.add('qualified');
//         } else {
//           freeShippingRemainder.innerHTML = remainderUSD.format((freeShippingThreshold - data.total_price) / 100);
//           freeShippingWrap.classList.remove('qualified');
//         }
//       });
//   }

//   // modifies PDP UI for the Appstle subscription purchase option
//   subscriptionPriceOutput() {
//     const addPriceUI = () => {
//       const priceWrapper = this.productWrapper.querySelector('.price__regular');
//       const priceCompare = document.createElement('s');
//       priceCompare.classList.add('price-subscribe', 'font-serif', 'text-light', 'visually-hidden');
//       const subscribeLabel = document.createElement('span');
//       subscribeLabel.classList.add('price-subscribe-label', 'small', 'visually-hidden');
//       subscribeLabel.innerText = 'Subscription';

//       priceWrapper.prepend(priceCompare);
//       priceWrapper.append(subscribeLabel);
//     };

//     const setPriceOutput = () => {
//       let planOneTime = null;
//       const planOptions = this.form.querySelectorAll('input[name=selling_plan]');
//       planOptions.forEach((option) => {
//         if (option.value === '') planOneTime = option;
//       });

//       const planSelected = this.form.querySelector('input[name=selling_plan]:checked');
//       const planSelectedAmt = planSelected.parentNode.querySelector('.appstle_subscription_amount').innerText;
//       const priceWrapper = this.productWrapper.querySelector('.price__regular');

//       priceWrapper.querySelector('.price-item').innerText = planSelectedAmt;

//       if (planSelected === planOneTime) {
//         priceWrapper.querySelector('.price-subscribe').classList.add('visually-hidden');
//         priceWrapper.querySelector('.price-subscribe-label').classList.add('visually-hidden');
//       } else {
//         const planOneTimeAmt = planOneTime.parentNode.querySelector('.appstle_subscription_amount').innerText;
//         priceWrapper.querySelector('.price-subscribe').innerText = planOneTimeAmt;
//         priceWrapper.querySelector('.price-subscribe').classList.remove('visually-hidden');
//         priceWrapper.querySelector('.price-subscribe-label').classList.remove('visually-hidden');
//       }
//     };

//     const setCTAlabels = (selected = true) => {
//       const btnBIN = this.form.querySelector('button.shopify-payment-button__button');
//       btnBIN.innerText = selected ? 'Subscribe' : 'Buy it now';
//     };

//     addPriceUI();

//     document.addEventListener('AppstleSubscription:SubscriptionWidget:SellingPlanDeSelected', (e) => {
//       setPriceOutput();
//       //setCTAlabels(false);
//     });
//     document.addEventListener('AppstleSubscription:SubscriptionWidget:SellingPlanSelected', (e) => {
//       setPriceOutput();
//       //setCTAlabels();
//       const appstleSelect = document.querySelector('select.appstle_select');
//       appstleSelect?.addEventListener('change', (e) => {
//         setPriceOutput();
//       });
//     });
//   }

//   handleErrorMessage(errorMessage = false) {
//     this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
//     if (!this.errorMessageWrapper) return;
//     this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

//     this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

//     if (errorMessage) {
//       this.errorMessage.textContent = errorMessage;
//     }
//   }
// }

// customElements.define('product-form', ProductForm);



// perfect working desktop and mobile add to cart 

// class ProductForm extends HTMLElement {
//   constructor() {
//     super();

//     this.form = this.querySelector('form');
//     this.form.querySelector('[name=id]').disabled = false;
//     this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

//     if (window.innerWidth >= 1024) {
//       this.cart = document.querySelector('cart-drawer-test');
//     } else {
//       this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
//     }

//     this.submitButton = this.querySelector('[type="submit"]');
//     this.productWrapper = this.form.dataset.productRef ? document.getElementById(this.form.dataset.productRef) : null;

//     if (this.productWrapper) this.subscriptionPriceOutput();
//   }

//   onSubmitHandler(evt) {
//     evt.preventDefault();
//     if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

//     this.handleErrorMessage();
//     this.submitButton.setAttribute('aria-disabled', true);
//     this.submitButton.classList.add('loading');
//     this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

//     const config = fetchConfig('javascript');
//     config.headers['X-Requested-With'] = 'XMLHttpRequest';
//     delete config.headers['Content-Type'];

//     const formData = new FormData(this.form);

//     // MOBILE
//     if (window.innerWidth < 1024) {
//       if (this.cart) {
//         const sections = this.cart.getSectionsToRender().map((section) => section.id);
//         sections.push('header'); // add header section
//         formData.append('sections', sections);
//         formData.append('sections_url', window.location.pathname);
//         this.cart.setActiveElement(document.activeElement);
//       }
//       config.body = formData;

//       fetch(`${routes.cart_add_url}`, config)
//         .then((response) => response.json())
//         .then((response) => {
//           if (response.status) {
//             this.handleErrorMessage(response.description);
//             const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
//             if (soldOutMessage) {
//               this.submitButton.setAttribute('aria-disabled', true);
//               this.submitButton.querySelector('span').classList.add('hidden');
//               soldOutMessage.classList.remove('hidden');
//             }
//             this.error = true;
//             return;
//           } else if (!this.cart) {
//             window.location = window.routes.cart_url;
//             return;
//           }

//           this.error = false;
//           const quickAddModal = this.closest('quick-add-modal');
//           if (quickAddModal) {
//             document.body.addEventListener(
//               'modalClosed',
//               () => {
//                 setTimeout(() => {
//                   this.cart.renderContents(response);
//                   this.updateHeader(response);
//                 });
//               },
//               { once: true }
//             );
//             quickAddModal.hide(true);
//           } else {
//             this.cart.renderContents(response);
//             this.updateHeader(response);
//           }
//         })
//         .catch((e) => console.error(e))
//         .finally(() => {
//           this.submitButton.classList.remove('loading');
//           if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
//           if (!this.error) this.submitButton.removeAttribute('aria-disabled');
//           this.querySelector('.loading-overlay__spinner').classList.add('hidden');
//           this.renderFreeShippingCTA();
//         });

//     } else {
//       // DESKTOP
//       formData.append('sections', 'cart-drawer-test,header');
//       formData.append('sections_url', window.location.pathname);
//       config.body = formData;

//       fetch(`${routes.cart_add_url}`, config)
//         .then((response) => response.json())
//         .then((response) => {
//           if (response.status) {
//             this.handleErrorMessage(response.description);
//             this.error = true;
//             return;
//           }

//           const drawerHTML = response.sections['cart-drawer-test'];
//           document.querySelector('cart-drawer-test').innerHTML = drawerHTML;
//           document.body.classList.add('cart-open');

//           this.updateHeader(response);
//         })
//         .catch((e) => console.error(e))
//         .finally(() => {
//           this.submitButton.classList.remove('loading');
//           if (!this.error) this.submitButton.removeAttribute('aria-disabled');
//           this.querySelector('.loading-overlay__spinner').classList.add('hidden');
//         });
//     }
//   }

//   // ✅ only update the cart bubble part inside header
//   updateHeader(response) {
//     if (response.sections && response.sections['header']) {
//       const tempDiv = document.createElement('div');
//       tempDiv.innerHTML = response.sections['header'];

//       const newBubble = tempDiv.querySelector('#cart-icon-bubble');
//       const currentBubble = document.querySelector('#cart-icon-bubble');

//       if (newBubble && currentBubble) {
//         currentBubble.innerHTML = newBubble.innerHTML;
//       }
//     }
//   }

//   renderFreeShippingCTA() {
//     const freeShippingSection = document.getElementById('section--free-shipping-cta');
//     if (!freeShippingSection) return;

//     const freeShippingThreshold = parseInt(freeShippingSection.dataset.threshold);
//     const freeShippingWrap = freeShippingSection.querySelector('.free-shipping-cta');
//     const freeShippingRemainder = freeShippingSection.querySelector('#freeShippingRemainder');
//     const remainderUSD = new Intl.NumberFormat('en-US', {
//       style: 'currency',
//       currency: 'USD'
//     });

//     fetch('/cart.js')
//       .then((response) => response.json())
//       .then((data) => {
//         if (data.total_price >= freeShippingThreshold) {
//           freeShippingWrap.classList.add('qualified');
//         } else {
//           freeShippingRemainder.innerHTML = remainderUSD.format((freeShippingThreshold - data.total_price) / 100);
//           freeShippingWrap.classList.remove('qualified');
//         }
//       });
//   }

//   subscriptionPriceOutput() {
//     const addPriceUI = () => {
//       const priceWrapper = this.productWrapper.querySelector('.price__regular');
//       const priceCompare = document.createElement('s');
//       priceCompare.classList.add('price-subscribe', 'font-serif', 'text-light', 'visually-hidden');
//       const subscribeLabel = document.createElement('span');
//       subscribeLabel.classList.add('price-subscribe-label', 'small', 'visually-hidden');
//       subscribeLabel.innerText = 'Subscription';

//       priceWrapper.prepend(priceCompare);
//       priceWrapper.append(subscribeLabel);
//     };

//     const setPriceOutput = () => {
//       let planOneTime = null;
//       const planOptions = this.form.querySelectorAll('input[name=selling_plan]');
//       planOptions.forEach((option) => {
//         if (option.value === '') planOneTime = option;
//       });

//       const planSelected = this.form.querySelector('input[name=selling_plan]:checked');
//       const planSelectedAmt = planSelected.parentNode.querySelector('.appstle_subscription_amount').innerText;
//       const priceWrapper = this.productWrapper.querySelector('.price__regular');

//       priceWrapper.querySelector('.price-item').innerText = planSelectedAmt;

//       if (planSelected === planOneTime) {
//         priceWrapper.querySelector('.price-subscribe').classList.add('visually-hidden');
//         priceWrapper.querySelector('.price-subscribe-label').classList.add('visually-hidden');
//       } else {
//         const planOneTimeAmt = planOneTime.parentNode.querySelector('.appstle_subscription_amount').innerText;
//         priceWrapper.querySelector('.price-subscribe').innerText = planOneTimeAmt;
//         priceWrapper.querySelector('.price-subscribe').classList.remove('visually-hidden');
//         priceWrapper.querySelector('.price-subscribe-label').classList.remove('visually-hidden');
//       }
//     };

//     addPriceUI();

//     document.addEventListener('AppstleSubscription:SubscriptionWidget:SellingPlanDeSelected', () => {
//       setPriceOutput();
//     });
//     document.addEventListener('AppstleSubscription:SubscriptionWidget:SellingPlanSelected', () => {
//       setPriceOutput();
//       const appstleSelect = document.querySelector('select.appstle_select');
//       appstleSelect?.addEventListener('change', () => {
//         setPriceOutput();
//       });
//     });
//   }

//   handleErrorMessage(errorMessage = false) {
//     this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
//     if (!this.errorMessageWrapper) return;
//     this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');
//     this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
//     if (errorMessage) {
//       this.errorMessage.textContent = errorMessage;
//     }
//   }
// }

// customElements.define('product-form', ProductForm);

// perfect working desktop and mobile add to cart 



class ProductForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form');
    this.form.querySelector('[name=id]').disabled = false;
    this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

    // if (window.innerWidth >= 1024) {
      this.cart = document.querySelector('cart-drawer-test');
    // } else {
      // this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    // }

    this.submitButton = this.querySelector('[type="submit"]');
    this.productWrapper = this.form.dataset.productRef ? document.getElementById(this.form.dataset.productRef) : null;

    if (this.productWrapper) this.subscriptionPriceOutput();
  }

  onSubmitHandler(evt) {
    evt.preventDefault();
    if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

    this.handleErrorMessage();
    this.submitButton.setAttribute('aria-disabled', true);
    this.submitButton.classList.add('loading');
    this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData(this.form);

    // MOBILE
    // if (window.innerWidth < 1024) {
      // if (this.cart) {
      //   const sections = this.cart.getSectionsToRender().map((section) => section.id);
      //   sections.push('header'); // add header section
      //   formData.append('sections', sections);
      //   formData.append('sections_url', window.location.pathname);
      //   this.cart.setActiveElement(document.activeElement);
      // }
      // config.body = formData;

      // fetch(`${routes.cart_add_url}`, config)
      //   .then((response) => response.json())
      //   .then((response) => {
      //     if (response.status) {
      //       this.handleErrorMessage(response.description);
      //       const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
      //       if (soldOutMessage) {
      //         this.submitButton.setAttribute('aria-disabled', true);
      //         this.submitButton.querySelector('span').classList.add('hidden');
      //         soldOutMessage.classList.remove('hidden');
      //       }
      //       this.error = true;
      //       return;
      //     } else if (!this.cart) {
      //       window.location = window.routes.cart_url;
      //       return;
      //     }

      //     this.error = false;
      //     const quickAddModal = this.closest('quick-add-modal');
      //     if (quickAddModal) {
      //       document.body.addEventListener(
      //         'modalClosed',
      //         () => {
      //           setTimeout(() => {
      //             this.cart.renderContents(response);
      //             this.updateHeader(response);
      //           });
      //         },
      //         { once: true }
      //       );
      //       quickAddModal.hide(true);
      //     } else {
      //       this.cart.renderContents(response);
      //       this.updateHeader(response);
      //     }
      //   })
      //   .catch((e) => console.error(e))
      //   .finally(() => {
      //     this.submitButton.classList.remove('loading');
      //     if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
      //     if (!this.error) this.submitButton.removeAttribute('aria-disabled');
      //     this.querySelector('.loading-overlay__spinner').classList.add('hidden');
      //     this.renderFreeShippingCTA();
      //   });

    // } else {
      // DESKTOP
      formData.append('sections', 'cart-drawer-test,header');
      formData.append('sections_url', window.location.pathname);
      config.body = formData;

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.handleErrorMessage(response.description);
            this.error = true;
            return;
          }

          const drawerHTML = response.sections['cart-drawer-test'];
          document.querySelector('cart-drawer-test').innerHTML = drawerHTML;
          document.body.classList.add('cart-open');

          this.updateHeader(response);
        })
        .catch((e) => console.error(e))
        .finally(() => {
          this.submitButton.classList.remove('loading');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading-overlay__spinner').classList.add('hidden');
        });
    // }
  }

  // ✅ only update the cart bubble part inside header
  updateHeader(response) {
    if (response.sections && response.sections['header']) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = response.sections['header'];

      const newBubble = tempDiv.querySelector('#cart-icon-bubble');
      const currentBubble = document.querySelector('#cart-icon-bubble');

      if (newBubble && currentBubble) {
        currentBubble.innerHTML = newBubble.innerHTML;
      }
    }
  }

  renderFreeShippingCTA() {
    const freeShippingSection = document.getElementById('section--free-shipping-cta');
    if (!freeShippingSection) return;

    const freeShippingThreshold = parseInt(freeShippingSection.dataset.threshold);
    const freeShippingWrap = freeShippingSection.querySelector('.free-shipping-cta');
    const freeShippingRemainder = freeShippingSection.querySelector('#freeShippingRemainder');
    const remainderUSD = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    });

    fetch('/cart.js')
      .then((response) => response.json())
      .then((data) => {
        if (data.total_price >= freeShippingThreshold) {
          freeShippingWrap.classList.add('qualified');
        } else {
          freeShippingRemainder.innerHTML = remainderUSD.format((freeShippingThreshold - data.total_price) / 100);
          freeShippingWrap.classList.remove('qualified');
        }
      });
  }

  subscriptionPriceOutput() {
    const addPriceUI = () => {
      const priceWrapper = this.productWrapper.querySelector('.price__regular');
      const priceCompare = document.createElement('s');
      priceCompare.classList.add('price-subscribe', 'font-serif', 'text-light', 'visually-hidden');
      const subscribeLabel = document.createElement('span');
      subscribeLabel.classList.add('price-subscribe-label', 'small', 'visually-hidden');
      subscribeLabel.innerText = 'Subscription';

      priceWrapper.prepend(priceCompare);
      priceWrapper.append(subscribeLabel);
    };

    const setPriceOutput = () => {
      let planOneTime = null;
      const planOptions = this.form.querySelectorAll('input[name=selling_plan]');
      planOptions.forEach((option) => {
        if (option.value === '') planOneTime = option;
      });

      const planSelected = this.form.querySelector('input[name=selling_plan]:checked');
      const planSelectedAmt = planSelected.parentNode.querySelector('.appstle_subscription_amount').innerText;
      const priceWrapper = this.productWrapper.querySelector('.price__regular');

      priceWrapper.querySelector('.price-item').innerText = planSelectedAmt;

      if (planSelected === planOneTime) {
        priceWrapper.querySelector('.price-subscribe').classList.add('visually-hidden');
        priceWrapper.querySelector('.price-subscribe-label').classList.add('visually-hidden');
      } else {
        const planOneTimeAmt = planOneTime.parentNode.querySelector('.appstle_subscription_amount').innerText;
        priceWrapper.querySelector('.price-subscribe').innerText = planOneTimeAmt;
        priceWrapper.querySelector('.price-subscribe').classList.remove('visually-hidden');
        priceWrapper.querySelector('.price-subscribe-label').classList.remove('visually-hidden');
      }
    };

    addPriceUI();

    document.addEventListener('AppstleSubscription:SubscriptionWidget:SellingPlanDeSelected', () => {
      setPriceOutput();
    });
    document.addEventListener('AppstleSubscription:SubscriptionWidget:SellingPlanSelected', () => {
      setPriceOutput();
      const appstleSelect = document.querySelector('select.appstle_select');
      appstleSelect?.addEventListener('change', () => {
        setPriceOutput();
      });
    });
  }

  handleErrorMessage(errorMessage = false) {
    this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
    if (!this.errorMessageWrapper) return;
    this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');
    this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
    if (errorMessage) {
      this.errorMessage.textContent = errorMessage;
    }
  }
}

customElements.define('product-form', ProductForm);












class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: `application/${type}`,
    },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add('drawer-open');
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove('drawer-open');
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add('drawer-open');
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.controls = this.querySelector('.slider-buttons');
    this.hasControls = this.controls ? true : false;

    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));

    if (this.hasControls) {
      this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
      this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
    }
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length > 1) {
      this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
      this.slidesPerPage = Math.floor(
        (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
      );
      this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage;
      this.update();
    } else {
      this.controls?.classList.add('disabled');
    }
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.hasControls) {
      let prevDisabled = false,
        nextDisabled = false;
      if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
        this.prevButton.setAttribute('disabled', 'disabled');
        prevDisabled = true;
      } else {
        this.prevButton.removeAttribute('disabled');
      }

      if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
        this.nextButton.setAttribute('disabled', 'disabled');
        nextDisabled = true;
      } else {
        this.nextButton.removeAttribute('disabled');
      }

      // hide slider controls if both are disabled
      if (prevDisabled && nextDisabled) this.controls.classList.add('disabled');
      else this.controls.classList.remove('disabled');
    }
  }

  isSlideVisible(element) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft + 5;
    const elementLeft = element.offsetLeft + 1;
    return elementLeft + element.clientWidth <= lastVisibleSlide && elementLeft >= this.slider.scrollLeft;
    //return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.slider.scrollTo({
      left: this.slideScrollPosition,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    //if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    if (this.hasControls) {
      this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
      this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    }

    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;

    if (this.hasControls) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.addEventListener('mouseover', this.focusInHandling.bind(this));
      this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
      this.addEventListener('focusin', this.focusInHandling.bind(this));
      this.addEventListener('focusout', this.focusOutHandling.bind(this));
    }

    this.play();
    this.autoplayButtonIsSetToPlay = true;
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) return;

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }
    this.slider.scrollTo({
      left: this.slideScrollPosition,
    });
  }

  update() {
    super.update();

    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');

    if (this.hasControls) {
      this.prevButton.removeAttribute('disabled');
    }

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    const focusedOnAutoplayButton =
      event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
    if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
    this.play();
  }

  focusInHandling(event) {
    const focusedOnAutoplayButton =
      event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
    if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
      this.play();
    } else if (this.autoplayButtonIsSetToPlay) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length
        ? 0
        : this.slider.scrollLeft + this.slider.querySelector('.slideshow__slide').clientWidth;
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }

  setSlideVisibility() {
    this.sliderItemsToShow.forEach((item, index) => {
      const button = item.querySelector('a');
      if (index === this.currentPage - 1) {
        if (button) button.removeAttribute('tabindex');
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (button) button.setAttribute('tabindex', '-1');
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.toggleAddButton(true, '', false);
    this.updatePickupAvailability();
    this.removeErrorMessage();

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
      this.updateShareUrl();
    }
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options
        .map((option, index) => {
          return this.options[index] === option;
        })
        .includes(false);
    });
  }

  updateMedia() {
    if (!this.currentVariant) return;
    if (!this.currentVariant.featured_media) return;

    const mediaGallery = document.getElementById(`MediaGallery-${this.dataset.section}`);
    mediaGallery.setActiveMedia(`${this.dataset.section}-${this.currentVariant.featured_media.id}`, true);

    const modalContent = document.querySelector(`#ProductModal-${this.dataset.section} .product-media-modal__content`);
    if (!modalContent) return;
    const newMediaModal = modalContent.querySelector(`[data-media-id="${this.currentVariant.featured_media.id}"]`);
    modalContent.prepend(newMediaModal);
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateShareUrl() {
    const shareButton = document.getElementById(`Share-${this.dataset.section}`);
    if (!shareButton || !shareButton.updateUrl) return;
    shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(
      `#ProductForm-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
    );
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updatePickupAvailability() {
    const pickUpAvailability = document.querySelector('pickup-availability');
    if (!pickUpAvailability) return;

    if (this.currentVariant && this.currentVariant.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.removeAttribute('available');
      pickUpAvailability.innerHTML = '';
    }
  }

  removeErrorMessage() {
    const section = this.closest('section');
    if (!section) return;

    const productForm = section.querySelector('product-form');
    if (productForm) productForm.handleErrorMessage();
  }

  renderProductInfo() {
    fetch(
      `${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${
        this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section
      }`
    )
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const destination = document.getElementById(`price-${this.dataset.section}`);
        const source = html.getElementById(
          `price-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );
        if (source && destination) destination.innerHTML = source.innerHTML;

        const price = document.getElementById(`price-${this.dataset.section}`);

        if (price) price.classList.remove('invisible');
        this.toggleAddButton(!this.currentVariant.available, window.variantStrings.soldOut);
      });
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    const productForm = document.getElementById(`product-form-${this.dataset.section}`);
    if (!productForm) return;
    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[name="add"] > span');
    if (!addButton) return;

    if (disable) {
      addButton.setAttribute('disabled', 'disabled');
      if (text) addButtonText.textContent = text;
    } else {
      addButton.removeAttribute('disabled');
      addButtonText.textContent = window.variantStrings.addToCart;
    }

    if (!modifyClass) return;
  }

  setUnavailable() {
    const button = document.getElementById(`product-form-${this.dataset.section}`);
    const addButton = button.querySelector('[name="add"]');
    const addButtonText = button.querySelector('[name="add"] > span');
    const price = document.getElementById(`price-${this.dataset.section}`);
    if (!addButton) return;
    addButtonText.textContent = window.variantStrings.unavailable;
    if (price) price.classList.add('invisible');
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}

customElements.define('variant-selects', VariantSelects);

class VariantRadios extends VariantSelects {
  constructor() {
    super();
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
    });
  }
}

customElements.define('variant-radios', VariantRadios);

class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.subDetailsToggles = this.mainDetailsToggle.querySelectorAll('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;
    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));

    this.subDetailsToggles.forEach((subDetails, i) => {
      subDetails.addEventListener('toggle', (e) => {
        if (subDetails.hasAttribute('open')) {
          // close the siblings
          this.subDetailsToggles.forEach((subDetails, i2) => {
            if (i !== i2) this.close(subDetails);
          });
        }
      });
    });
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close(this.mainDetailsToggle);
    }, 50);
  }

  close(detailsEl) {
    detailsEl.removeAttribute('open');
    detailsEl.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
  }
}

customElements.define('header-menu', HeaderMenu);

class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');

    this.detailsContainer.addEventListener('keyup', (event) => event.code.toUpperCase() === 'ESCAPE' && this.close());
    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));
    this.querySelector('button[type="button"]').addEventListener('click', this.close.bind(this));

    this.summaryToggle.setAttribute('role', 'button');
  }

  isOpen() {
    return this.detailsContainer.hasAttribute('open');
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest('details').hasAttribute('open') ? this.close() : this.open(event);
  }

  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close(false);
  }

  open(event) {
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    event.target.closest('details').setAttribute('open', true);
    document.body.addEventListener('click', this.onBodyClickEvent);
    document.body.classList.add('overflow-hidden');

    trapFocus(
      this.detailsContainer.querySelector('[tabindex="-1"]'),
      this.detailsContainer.querySelector('input:not([type="hidden"])')
    );
  }

  close(focusToggle = true) {
    removeTrapFocus(focusToggle ? this.summaryToggle : null);
    this.detailsContainer.removeAttribute('open');
    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.body.classList.remove('overflow-hidden');
  }
}

customElements.define('details-modal', DetailsModal);

class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.notification.classList.remove('active');

    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    const sectionsToRender = this.getSectionsToRender();

    sectionsToRender.forEach((section) => {
      const sectionElement = document.getElementById(section.id);
      if (sectionElement) {
        const sectionContent = parsedState.sections && parsedState.sections[section.id];
        if (section.id === 'cart-notification-product') {
          const newCartItem = this.createCartItem(sectionContent, section.selector);
          this.insertCartItem(sectionElement, newCartItem);
        } else {
          sectionElement.innerHTML = this.getSectionInnerHTML(sectionContent, section.selector);
        }
      }
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  createCartItem(sectionContent, selector) {
    const cartItemWrapper = document.createElement('div');
    cartItemWrapper.classList.add('cart-notification-product-item');
    cartItemWrapper.innerHTML = this.getSectionInnerHTML(sectionContent, selector);
    return cartItemWrapper;
  }

  insertCartItem(sectionElement, newCartItem) {
    const existingCartItems = sectionElement.querySelectorAll('.cart-notification-product-item');
    const maxCartItems = 2;

    sectionElement.insertBefore(newCartItem, existingCartItems[0]);

    if (existingCartItems.length >= maxCartItems) {
      for (let i = maxCartItems; i < existingCartItems.length; i++) {
        sectionElement.removeChild(existingCartItems[i]);
      }
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);

// poached from https://github.com/LaNsHoR/native-html-tabs
class TabTitle extends HTMLElement {
  constructor() {
    super();
    //this.setActiveContent();
    this.addEventListener('click', () => this.click());
  }

  click() {
    if (this.hasAttribute('disabled')) return;
    this.setActiveContent();
  }

  setActiveContent() {
    const position = this.position();
    const instanceId = this.parentElement?.dataset.titles;
    const tabs = this.parentElement?.children;
    const contents = document.querySelector('.tab-panels[data-panels="' + instanceId + '"]')?.children;
    //const eventOptions = { bubbles: true, composed: true, cancelable: true };

    if (!contents || !tabs) return;

    Array.from(contents).forEach((content, index) => {
      if (index == position || !content.hasAttribute('active')) return;
      content.removeAttribute('active');
      //content.dispatchEvent(new CustomEvent('activechange', eventOptions));
    });
    Array.from(tabs).forEach((tab, index) => {
      if (index == position || !tab.hasAttribute('active')) return;
      tab.removeAttribute('active');
      tab.setAttribute('aria-selected', 'false');
      //tab.dispatchEvent(new CustomEvent('activechange', eventOptions));
    });
    if (!contents[position]?.hasAttribute('active')) {
      contents[position]?.setAttribute('active', '');
      //contents[position]?.dispatchEvent(new CustomEvent('activechange', eventOptions));
    }
    if (!this.hasAttribute('active')) {
      this.setAttribute('active', '');
      this.setAttribute('aria-selected', 'true');
      //this.dispatchEvent(new CustomEvent('activechange', eventOptions));
    }
  }

  position() {
    return Array.from(this.parentElement?.children).indexOf(this);
  }
}
customElements.define('tab-title', TabTitle);

if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super(), (this.modalContent = this.querySelector('[id^="QuickAddInfo-"]'));
      }
      hide(e = !1) {
        const t = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        t && t.setActiveElement(this.openedBy),
          (this.modalContent.innerHTML = ''),
          e && (this.openedBy = null),
          super.hide();
      }
      show(e) {
        e.setAttribute('aria-disabled', !0),
          e.classList.add('loading'),
          e.querySelector('.loading-overlay__spinner').classList.remove('hidden'),
          fetch(e.getAttribute('data-product-url'))
            .then((t) => t.text())
            .then((t) => {
              const o = new DOMParser().parseFromString(t, 'text/html');
              (this.productElement = o.querySelector('section[id^="MainProduct-"]')),
                this.preventDuplicatedIDs(),
                this.removeDOMElements(),
                this.setInnerHTML(this.modalContent, this.productElement.innerHTML),
                window.Shopify && Shopify.PaymentButton && Shopify.PaymentButton.init(),
                window.ProductModel && window.ProductModel.loadShopifyXR(),
                this.removeGalleryListSemantic(),
                this.preventVariantURLSwitching(),
                super.show(e);
            })
            .finally(() => {
              e.removeAttribute('aria-disabled'),
                e.classList.remove('loading'),
                e.querySelector('.loading-overlay__spinner').classList.add('hidden');
            });
      }
      setInnerHTML(e, t) {
        (e.innerHTML = t),
          e.querySelectorAll('script').forEach((t) => {
            const o = document.createElement('script');
            Array.from(t.attributes).forEach((e) => {
              o.setAttribute(e.name, e.value);
            }),
              o.appendChild(document.createTextNode(t.innerHTML)),
              t.parentNode.replaceChild(o, t);
          });
      }
      preventVariantURLSwitching() {
        this.modalContent.querySelector('variant-radios,variant-selects').setAttribute('data-update-url', 'false');
      }
      removeDOMElements() {
        const e = this.productElement.querySelector('pickup-availability');
        e && e.remove();
        const t = this.productElement.querySelector('product-modal');
        t && t.remove();
      }
      preventDuplicatedIDs() {
        const e = this.productElement.dataset.section;
        (this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(e, `quickadd-${e}`)),
          this.productElement.querySelectorAll('variant-selects, variant-radios').forEach((t) => {
            t.dataset.originalSection = e;
          });
      }
      removeGalleryListSemantic() {
        const e = this.modalContent.querySelector('[id^="Slider-Gallery"]');
        e &&
          (e.setAttribute('role', 'presentation'),
          e.querySelectorAll('[id^="Slide-"]').forEach((e) => e.setAttribute('role', 'presentation')));
      }
    }
  );
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter((element) => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector('.facets__summary').outerHTML = source.querySelector('.facets__summary').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) =>
      element.addEventListener('change', this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
